import { type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { useApiQuery } from '../api/hooks.js';
import { queryKeys } from '../api/query-keys.js';

/**
 * Shared reference hook for the currency vocabulary.
 *
 * Both the properties and exchange-rates modules need the same dropdown, so it
 * lives in the admin's shared reference layer rather than inside either module.
 */

const CURRENCY_OPTIONS_PATH = '/v1/currencies/options';

const optionSchema = z.object({
  value: z.string(),
  label: z.string(),
  meta: z.record(z.string(), z.string().nullable()).optional(),
});

const currencyOptions = z.array(optionSchema).transform((options) =>
  options.map((option) => ({
    code: option.value,
    name: option.label,
    symbol: option.meta?.symbol ?? null,
  })),
);

/** Reference data: the vocabularies a currency picker is written in. */
export const currencyOption = z.object({
  code: z.string(),
  name: z.string(),
  symbol: z.string().nullable(),
});

export type CurrencyOption = z.infer<typeof currencyOption>;

export function useCurrencies(): UseQueryResult<readonly CurrencyOption[]> {
  return useApiQuery({
    queryKey: queryKeys.reference.currencies,
    path: CURRENCY_OPTIONS_PATH,
    schema: currencyOptions,
  });
}
